<template>
    <map-map v-model:zoom="mapZoom"
             :min-zoom="minMapZoom"
			 :focus-elements="focusElements"
             ref="map"
             v-model:bounds="mapBounds"
             @ready="handleLoadedMap"
			 :cursor="[editModes.CreateMarker, editModes.CreateTrackSpotControl, editModes.CreateTrackSpotResult, editModes.CreateTrackSpotNavigation, editModes.MoveTrackSpot, editModes.MoveMarker].includes(editMode) ? 'crosshair' : 'grab'"
             :rounded="rounded"
             :center="mapCenter">

		<div v-if="(participationClasses && participationClasses.length > 1)" class="absolute z-50 m-10 max-w-screen-80">
			<form-select v-model="visibleParticipationClassKeys"
						 :options="participationClasses"
						 multiple
						 :color="event.color"
						 hide-details
						 :placeholder="$t('placeholder.select_participation_class')"
						 value-prop="_key"
						 text-prop="name"/>
		</div>

		<lazy-event-marker v-if="showEventMarker && !editMode" :event="event"/>

		<lazy-map-polyline v-if="inTrackCreationMode && trackCreationLatLngs.length" :lat-lngs="trackCreationLatLngs" :zoom="mapZoom" distance-info/>

        <template v-for="track in visibleTracks" :key="`${track.model_name}_${track.id}`">
            <lazy-track-polyline
                :downloadable="downloadable"
                :zoom="mapZoom"
                :weight="editMode && [editModes.CreateTrackSpotResult, editModes.CreateTrackSpotControl, editModes.CreateTrackSpotNavigation, editModes.MoveTrackSpot].indexOf(editMode) > -1 ? 4 : 3"
                :unfocused="editMode && ((editMode === editModes.MoveTrack && moveElement && moveElement.id !== track.id) || [editModes.CreateTrackSpotResult, editModes.CreateTrackSpotControl, editModes.CreateTrackSpotNavigation, editModes.MoveTrackSpot, editModes.MoveTrack].indexOf(editMode) < 0)"
                :draggable="editMode === editModes.MoveTrack && moveElement && moveElement.id === track.id"
                @moving="latLngData => handleMovingTrack(track, latLngData)"
                @click="latLngData => handleTrackClick(track, latLngData)"
                :track="track"/>
        </template>

        <template v-for="trackSpot in visibleTrackSpots" :key="`${trackSpot.model_name}_${trackSpot.id}`">
            <lazy-track-spot-marker :track-spot="trackSpot"
                               :unfocused="editMode && (![editModes.CreateTrackSpotControl, editModes.CreateTrackSpotNavigation, editModes.CreateTrackSpotResult, editModes.MoveTrackSpot].includes(editMode) || (moveElement && moveElement.id !== trackSpot.id))"
									@click="handleTrackSpotClick(trackSpot)"/>
        </template>

        <template v-for="checkpoint in visibleCheckpoints" :key="`${checkpoint.model_name}_${checkpoint.id}`">
            <lazy-checkpoint-marker
                :unfocused="editMode && (![editModes.CreateMarker, editModes.MoveMarker].includes(editMode) || (moveElement && moveElement.id !== checkpoint.id))"
				@click="handleCheckpointClick(checkpoint)"
                :size="24"
                :checkpoint="checkpoint"/>
        </template>

		<slot v-bind:inPrintMode="isPrinting"/>

        <template #footer>
            <slot name="footer"/>
        </template>

        <template #controls>
            <btn-group v-if="showEventMarker || (withTypesFilter && ((trackSpots && trackSpots.length) || (checkpoints && checkpoints.length) || (tracks && tracks.length)))" vertical>
				<btn v-if="showEventMarker" small icon @click.stop="mapCenter = [event.lat, event.lng]" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.show_event_on_map')">
					<Icon :icon="icons.locate_event"/>
				</btn>
				<btn v-if="withTypesFilter && ((trackSpots && trackSpots.length) || (checkpoints && checkpoints.length) || (tracks && tracks.length))"
					 :color="!mapTypesFilter.checkpoints || !mapTypesFilter.tracks || mapTypesFilter.track_spots.length !== 3 ? event.color ?? $config.public.colors.secondary : null"
					 small icon @click.stop="isFilterModalVisible = true" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.filter')">
					<Icon :icon="icons.filter"/>
				</btn>
            </btn-group>

			<slot name="controls"/>

			<btn-group v-if="!editMode && ($device.isDesktop || $slots.action_controls)" vertical>
				<btn v-if="$device.isDesktop" small icon @click.stop="printMap" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.print')" :loading="isPrinting">
					<Icon :icon="icons.print"/>
				</btn>
				<slot name="action_controls"/>
			</btn-group>

            <template v-if="editable">
                <template v-if="!editMode">
                    <btn-group v-if="tracks" vertical>
						<btn small icon @click.stop="editMode = editModes.UploadTrack" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.track.upload')">
							<Icon :icon="icons.upload"/>
						</btn>
						<btn small icon @click.stop="editMode = editModes.CreateTrack" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.track.draw')">
							<Icon :icon="icons.track"/>
						</btn>
                    </btn-group>
                    <btn-group v-if="checkpoints || trackSpots" vertical>
						<btn v-if="checkpoints" small icon @click.stop="editMode = editModes.CreateMarker" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.create_checkpoint')">
							<Icon :icon="icons.checkpoint"/>
						</btn>

						<template v-if="trackSpots">
							<btn small icon @click.stop="editMode = editModes.CreateTrackSpotResult" class="w-36 min-w-36 h-36 min-h-36" :disabled="!tracks || !tracks.length" :title="$t('action.track_spot.2.create')">
								<Icon :icon="icons.tracking_spot"/>
							</btn>

							<btn small icon @click.stop="editMode = editModes.CreateTrackSpotControl" class="w-36 min-w-36 h-36 min-h-36" :disabled="!tracks || !tracks.length" :title="$t('action.track_spot.3.create')">
								<Icon :icon="icons.track_spot_control"/>
							</btn>
							<btn small icon @click.stop="editMode = editModes.CreateTrackSpotNavigation" class="w-36 min-w-36 h-36 min-h-36" :disabled="!tracks || !tracks.length" :title="$t('action.track_spot.1.create')">
								<Icon :icon="icons.track_spot_navigation"/>
							</btn>
						</template>
                    </btn-group>
                </template>
            </template>
            <btn-group v-if="editMode && !hideEditControls" vertical>
                <template v-if="moveElement">
					<btn small icon @click.stop="saveMoveChanges" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.save')" color="success">
						<Icon :icon="icons.check"/>
					</btn>
					<btn small icon @click.stop="cancelMoveChanges" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.cancel')" color="warning">
						<Icon :icon="icons.close"/>
					</btn>
                </template>
                <template v-else-if="inTrackCreationMode">
					<btn small icon @click.stop="saveDrawingTrack" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.save')" color="success">
						<Icon :icon="icons.save"/>
					</btn>
					<btn small icon @click.stop="cancelCreatingTrack" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.cancel')" color="warning">
						<Icon :icon="icons.close"/>
					</btn>
                </template>
				<btn v-else small icon @click="editMode = null" class="w-36 min-w-36 h-36 min-h-36" :title="$t('action.cancel')" color="warning">
					<Icon :icon="icons.close"/>
				</btn>
            </btn-group>

            <input style="display: none" type="file" name="gpx" accept=".gpx" ref="gpxUploader"
                   @change="handleUploadedGpxFile"/>

        </template>

        <div v-if="editMode && ![editModes.UploadTrack].includes(editMode)" class="absolute z-50 bg-white m-10 bottom-0 right-0 rounded px-10 py-5">
            <p class="mb-0 leading-spaced">{{ $t(`text.map_modes.${editMode}`) }}</p>
        </div>

		<client-only>
			<lazy-modal v-if="withTypesFilter" v-model="isFilterModalVisible" :title="$t('action.filter')">
				<template v-for="trackSpotType in trackSpotTypes" :key="`track_spot_type-${trackSpotType}`">
					<form-switch v-model="mapTypesFilter.track_spots"
								 v-if="trackSpots && trackSpots.filter(spot => spot.type === trackSpotType).length"
								 force-row
								 hide-details :color="event.color"
								 :label="$t(`enum.track_spot_type.${trackSpotType}.title`)" :true-value="trackSpotType"/>
				</template>
				<form-switch v-if="checkpoints && checkpoints.length" v-model="mapTypesFilter.checkpoints"
							 force-row
							 hide-details :color="event.color" :label="$t(`label.checkpoint`)"/>
				<form-switch v-if="tracks && tracks.length" v-model="mapTypesFilter.tracks"
							 force-row
							 hide-details :color="event.color" :label="$t(`label.track`)"/>
			</lazy-modal>

			<slide-sheet v-if="selectedTrack" v-model="isTrackDetailsModalVisible" :width="420">
				<template #default>
					<track-popup-card :track="selectedTrack"
									  :downloadable="downloadable"
									  :editable="editable"
									  @download="downloadTrack(selectedTrack)"
									  @upload="replaceTrackLatLngsByFile(selectedTrack)"
									  @edit="editTrack(selectedTrack)"
									  @destroy="destroyTrack(selectedTrack)"
									  @move="moveTrack(selectedTrack)"
					/>
				</template>
			</slide-sheet>

			<slide-sheet v-if="selectedCheckpoint" v-model="isCheckpointDetailsModalVisible" :width="420">
				<template #default>
					<checkpoint-popup-card :checkpoint="selectedCheckpoint"
									  :editable="editable"
									  @edit="editCheckpoint(selectedCheckpoint)"
									  @destroy="destroyCheckpoint(selectedCheckpoint)"
									  @move="moveCheckpoint(selectedCheckpoint)"
					/>
				</template>
			</slide-sheet>

			<slide-sheet v-if="selectedTrackSpot" v-model="isTrackSpotDetailsModalVisible" :width="420">
				<template #default>
					<track-spot-popup-card :track-spot="selectedTrackSpot"
										   :editable="editable"
										   @edit="editTrackSpot(selectedTrackSpot)"
										   @destroy="destroyTrackSpot(selectedTrackSpot)"
										   @move="moveTrackSpot(selectedTrackSpot)"
					/>
				</template>
			</slide-sheet>
		</client-only>

    </map-map>
</template>

<script>
import {
	mdiCalendar,
	mdiFilterOutline,
	mdiMapMarkerPlusOutline,
	mdiNavigationOutline,
	mdiPrinterOutline,
	mdiTimelineCheckOutline,
	mdiTimelineClockOutline,
	mdiVectorPolylinePlus
} from "@mdi/js";
import {MapEditMode, TrackSpotType, TrackSpotSubtype} from "@spoferan/spoferan-ts-core";
import {GeolocationService} from "../../services/GeolocationService";
import {formatLatLng} from "@spoferan/spoferan-ts-core";
import {checkIcon, closeIcon, saveIcon, uploadIcon} from "../../config";

export default {
    name: "EventMap",

	emits: ['dragged', 'track-download', 'track-click', 'track-create', 'track-update', 'checkpoint-create', 'checkpoint-destroy', 'checkpoint-edit', 'checkpoint-moved', 'track-spot-create', 'track-spot-destroy', 'track-spot-edit', 'track-spot-moved', 'track-spot-refresh', 'track-edit', 'track-destroy', 'track-moved', 'contextmenu', 'update:zoom', 'update:bounds', 'update:typesFilter', 'update:moveModel', 'update:selectedParticipationClassKeys', 'update:mode'],

    props: {
        event: {
            type: Object,
            required: true
        },

        // The geolocation where to center the map on initialization
        center: {
            type: [Object, Array],
            default: null
        },

        // The participation classes to select and filter
        participationClasses: {
            type: Array,
            default: null
        },

        // The ids of the currently selected and filtered participation classes
        selectedParticipationClassKeys: {
            type: Array,
            default: () => []
        },

        // Whether the tracks can be downloaded
        downloadable: {
            type: Boolean,
            default: false
        },

        // Whether the map elements visibility can be controlled
        withTypesFilter: {
            type: Boolean,
            default: false
        },

        typesFilter: {
            type: Object,
            default: () => {
                return {checkpoints: true, tracks: true, track_spots: [1,2,3]}
            }
        },

        // The current edit mode of the map
        mode: {
            type: String,
            default: null
        },

        checkpoints: {
            type: Array,
            default: null
        },

        tracks: {
            type: Array,
            default: null
        },

        trackSpots: {
            type: Array,
            default: null
        },

        // An array of additional object containing either a 'lat' and 'lng' prop or a 'latlngs' which is zoomed to on initial map load
        additionalZoomElements: {
            type: [Array, Object],
            default: null
        },

        // States if a filter on the classes shall be written and read from the query params
        withQueryFilter: {
            type: Boolean,
            default: false
        },

        // States if the user can edit the elements on the map by himself
        editable: {
            type: Boolean,
            default: false
        },

        // Whether the user can control the edit state by himself
        hideEditControls: {
            type: Boolean,
            default: false
        },

        zoom: {
            type: Number,
            default: 15
        },

        // Whether the event marker itself shall be hidden
        hideEventMarker: {
            type: Boolean,
            default: false
        },

        // The bounds geolocation of the map
        bounds: {
            type: [Array, Object],
            default: null,
        },

        // States if a border radius shall be applied on the map
        rounded: {
            type: Boolean,
            default: false
        },

        // The model to keep in sync with the element currently being moved
        moveModel: {
            type: Object,
            default: null
        },

		// An array of additional object containing either a 'lat' and 'lng' prop or a 'latlngs' which is zoomed to on initial map load
		focusElements: {
			type: [Array, Object],
			default: null,
		}
    },

    data() {
        return {

            // The underlying Leaflet map instance
            mapObject: null,

			// The explicitly selected participation classes whose map elements shall be exclusively shown
			// If empty, all elements are shown
            visibleParticipationClassKeys: this.selectedParticipationClassKeys,

            showFilter: false,

            mapZoom: this.zoom,
            minMapZoom: 4,

            trackCreationLatLngs: [],

            mapCenter: this.center,
            mapBounds: this.bounds,
			mapTypesFilter: this.typesFilter,

            editMode: this.mode,
            prepareEditMode: null, // The edit mode being set after a map move
            editModes: MapEditMode,

            trackSpotTypes: TrackSpotType,

            // The map element model currently being moved
            moveElement: this.moveModel,

            // The lat lng object of the position where the user clicked at a track the last time
            // Required to zoom the map to that position when moving tracks
            lastTrackClickLatLng: null,

            // The data of the track currently being replaced by a GPX upload
			replacingTrack: null,

            isLoading: false,

			isFilterModalVisible: false,

			selectedTrack: null,
			originalMovingTrackData: {},
			isTrackDetailsModalVisible: false,

			selectedCheckpoint: null,
			originalMovingCheckpointData: {},
			isCheckpointDetailsModalVisible: false,

			selectedTrackSpot: null,
			originalMovingTrackSpotData: {},
			isTrackSpotDetailsModalVisible: false,

			isPrinting: false,

            icons: {
				close: closeIcon,
				save: saveIcon,
                tracking_spot: mdiTimelineClockOutline,
                track_spot_control: mdiTimelineCheckOutline,
                track_spot_navigation: mdiNavigationOutline,
                locate_event: mdiCalendar,
                track: mdiVectorPolylinePlus,
                checkpoint: mdiMapMarkerPlusOutline,
				check: checkIcon,
				upload: uploadIcon,
				filter: mdiFilterOutline,
				print: mdiPrinterOutline
            }
        }
    },

    computed: {
        showEventMarker() {
            return !this.hideEventMarker && (!this.trackSpots || !this.trackSpots.length) && this.event.lat && this.event.lng
        },

        inMarkerCreationMode() {
            return this.editMode === MapEditMode.CreateMarker
        },

        inTrackCreationMode() {
            return this.editMode === MapEditMode.CreateTrack
        },

		visibleTrackSpots() {
			// We want to display the start and finish spots of participation classes when only their tracks are selected to symbolize the start and finish of their tracks
			return this.trackSpots ? this.trackSpots.filter(trackSpot => {
				return trackSpot.lat &&
					trackSpot.lng &&
					(
						(this.mapTypesFilter.track_spots.includes(trackSpot.type) && this.existsForVisibleParticipationClasses(trackSpot)) ||
						(this.mapTypesFilter.tracks && (this.isStartTrackSpotForVisibleParticipationClasses(trackSpot) || this.isFinishTrackSpotForVisibleParticipationClasses(trackSpot)))
					)
			}) : []
		},

		visibleCheckpoints() {
			return this.checkpoints ? this.checkpoints.filter(checkpoint => checkpoint.lat && checkpoint.lng && this.mapTypesFilter.checkpoints && this.existsForVisibleParticipationClasses(checkpoint)) : []
		},

		visibleTracks() {
			return this.tracks ? this.tracks.filter(track => track.latlngs?.length && this.mapTypesFilter.tracks && this.existsForVisibleParticipationClasses(track)) : []
		}
    },

    watch: {
        mode(mode) {
            this.editMode = mode
        },

        editMode(editMode) {
            this.$emit('update:mode', editMode);

            this.showEditModeHelpAlert(editMode);
            this.handleChangedEditMode();
        },

        bounds(bounds) {
            this.mapBounds = bounds;
        },

        mapBounds(mapBounds) {
            this.$emit('update:bounds', mapBounds)
        },

		typesFilter(typesFilter) {
			this.mapTypesFilter = typesFilter;
		},

		mapTypesFilter(mapTypesFilter) {
			this.$emit('update:typesFilter', mapTypesFilter)
		},

        zoom(zoom) {
            this.mapZoom = zoom
        },

        mapZoom(mapZoom) {
            this.$emit('update:zoom', mapZoom);
        },

        moveModel(moveModel) {
            this.moveElement = moveModel
        },

        moveElement(moveElement) {
            this.$emit('update:moveModel', moveElement);
        },

        selectedParticipationClassKeys(selectedParticipationClassKeys) {
            this.visibleParticipationClassKeys = selectedParticipationClassKeys
        },

		visibleParticipationClassKeys: {
			handler: function(visibleParticipationClassKeys) {
				this.$emit('update:selectedParticipationClassKeys', visibleParticipationClassKeys);

				// Update the query
				if (this.withQueryFilter) {
					const existingQuery = {...this.$route.query};
					if (!visibleParticipationClassKeys.length) {
						delete existingQuery['class'];
					} else {
						existingQuery.class = visibleParticipationClassKeys.join(',')
					}
					this.$router.replace({query: existingQuery})
				}

				this.$nextTick(function () {
					this.zoomToContent();
				});
			},
			deep: true
		},

        inTrackCreationMode() {
            this.trackCreationLatLngs = [];
        },

        additionalZoomElements() {
            this.zoomToContent();
        }
    },

    mounted() {
        this.$nextTick(function () {
            this.showFilter = this.$breakpoint.mdAndUp
        });
    },

    methods: {

        handleChangedEditMode() {
            if (this.editMode && this.editMode === MapEditMode.UploadTrack) {
                this.chooseGpxFile();
            } else if (this.editMode && this.editMode === MapEditMode.MoveTrackSpot) {
                this.minMapZoom = 18;
            } else if (this.editMode && [MapEditMode.MoveTrack].indexOf(this.editMode) > -1) {
                this.minMapZoom = 16;
            } else if (this.editMode && [MapEditMode.CreateTrackSpotResult, MapEditMode.CreateTrackSpotControl, MapEditMode.CreateTrackSpotNavigation].indexOf(this.editMode) > -1) {
                this.minMapZoom = 18;
            } else {
                this.minMapZoom = 4;
            }
        },

        /**
         * Handles the event when the map was fully loaded
         */
        handleLoadedMap(mapObject) {
			this.handleChangedEditMode();

            this.mapObject = mapObject;
            setTimeout(() => {
                this.zoomToContent();
            }, 50); // Make sure map is fully loaded, so the zoom works

			mapObject.on('move', (event) => {

				// Manual user drag event
				if (event.originalEvent) {
					this.$emit('dragged');
				}
			});

            mapObject.on('mousemove', (event) => {
                const formattedLatLng = formatLatLng(event.latlng);
                if (this.inTrackCreationMode) {
                    this.trackCreationLatLngs[Math.max(0, this.trackCreationLatLngs.length - 1)] = formattedLatLng
                }
            });

            mapObject.on('click', (event) => {
                const formattedLatLng = formatLatLng(event.latlng);
                if (this.inMarkerCreationMode) {
                    this.saveCreatingCheckpoint(formattedLatLng);
                } else if (this.inTrackCreationMode) {
                    // Add before our cursor latlng
                    this.trackCreationLatLngs.splice(Math.max(0, this.trackCreationLatLngs.length - 1), 0, formattedLatLng)
                } else if (this.editMode === MapEditMode.MoveMarker) {
					this.moveElement.lat = formattedLatLng.lat;
					this.moveElement.lng = formattedLatLng.lng;
                }
            });

            mapObject.on('contextmenu', (event) => {
                this.$emit('contextmenu', event);

                if (this.inTrackCreationMode && this.trackCreationLatLngs.length) {
                    this.trackCreationLatLngs.splice(this.trackCreationLatLngs.length - 1, 1)
                }
            });

            mapObject.on('moveend', () => {
                if (this.prepareEditMode) {
                    setTimeout(() => {
                        this.editMode = this.prepareEditMode;
                        this.prepareEditMode = null;
                     }, 10); // Just a little timeout to let the map render correctly after move
                }
            });
        },

		isStartTrackSpotForVisibleParticipationClasses(trackSpot) {
			if (!trackSpot || trackSpot.type !== TrackSpotType.Result) {
				return false;
			}

			// All selected
			if (!this.visibleParticipationClassKeys || !this.visibleParticipationClassKeys.length) {
				return trackSpot.participationClasses?.some(participationClass => participationClass.pivot?.position === 1)
			}

			return this.visibleParticipationClassKeys.some(participationClassKey => {
				return trackSpot.participationClasses?.find(participationClass => participationClass._key === participationClassKey && participationClass.pivot?.position === 1)
			})
		},

		isFinishTrackSpotForVisibleParticipationClasses(trackSpot) {
			if (!trackSpot || trackSpot.type !== TrackSpotType.Result) {
				return false;
			}

			// All selected
			if (!this.visibleParticipationClassKeys || !this.visibleParticipationClassKeys.length) {
				return trackSpot.participationClasses?.some(participationClass => participationClass.pivot?.is_finish)
			}

			return this.visibleParticipationClassKeys.some(participationClassKey => {
				return trackSpot.participationClasses?.find(participationClass => participationClass._key === participationClassKey && participationClass.pivot?.is_finish)
			})
		},

        existsForVisibleParticipationClasses(mapObject) {
            if (!mapObject) {
                return false;
            }

			// All selected
            if (!this.visibleParticipationClassKeys || !this.visibleParticipationClassKeys.length) {
                return true;
            }

            const mapObjectClassKeys = mapObject.participationClasses.map(participationClass => participationClass._key);

            return this.visibleParticipationClassKeys.some(participationClassKey => mapObjectClassKeys.includes(participationClassKey))
        },

		// TODO: Expose when migrating to Nuxt 3
        flyTo(latLng, focus = false, zoom = null) {
            if (this.$refs.map) {
                this.$refs.map.flyTo(latLng, focus, zoom);
            }
        },

        /**
         * Zooms the map to a level that every content is visible.
         */
        zoomToContent() {
            if (this.mapObject && !this.center) {
                const latLngs = [];

                if (this.showEventMarker) {
                    latLngs.push(formatLatLng([this.event.lat, this.event.lng]))
                }

				this.visibleCheckpoints.forEach(checkpoint => {
					if (checkpoint.lat && checkpoint.lng) {
						latLngs.push(formatLatLng([checkpoint.lat, checkpoint.lng]))
					}
				});

				this.visibleTrackSpots.forEach(trackSpot => {
					if (trackSpot.lat && trackSpot.lng) {
						latLngs.push(formatLatLng([trackSpot.lat, trackSpot.lng]))
					}
				});

				this.visibleTracks.forEach(track => {
					if (track.latlngs && track.latlngs.length) {
						latLngs.push(...track.latlngs)
					}
				});

                const additionalZoomElements = Array.isArray(this.additionalZoomElements) ? this.additionalZoomElements : [this.additionalZoomElements];
                if (additionalZoomElements && additionalZoomElements.length) {
                    additionalZoomElements.forEach(element => {
                        if (element) {
                            if (element.latlngs && element.latlngs.length) {
                                latLngs.push(...element.latlngs)
                            } else if (element.lat && element.lng) {
                                latLngs.push(formatLatLng([element.lat, element.lng]))
                            }
                        }
                    });
                }

                if (latLngs.length) {
                    this.mapObject.fitBounds(latLngs, {
                        padding: [8, 8] // Some padding to not have the outer map elements to be on the corners of the map
                    });
                } else if (!this.center) {
                    this.mapCenter = latLngs[0]
                }
            }
        },

        saveDrawingTrack(trackData = {}) {
			const latLngs = this.trackCreationLatLngs;

			// remove our cursor marker
			latLngs.splice(latLngs.length - 1, 1);

			// If no distance is specified, we calculate it
			if (!trackData.distance) {
				trackData.distance = GeolocationService.getDistanceOfSet(latLngs);
			}

			if (latLngs && latLngs.length) {
				this.$emit('track-create', {...trackData, latlngs: latLngs});
			}
        },

        cancelCreatingTrack() {
            if (this.trackCreationLatLngs && this.trackCreationLatLngs.length > 1) {
                this.$confirm({
                    title: this.$t('confirm.tracks.cancel_create.title'),
                    text: this.$t('confirm.tracks.cancel_create.message'),
                    type: 'warning'
                }).then(({confirmed}) => {
                    if (confirmed) {
                        this.editMode = null;
                    }
                })
            } else {
                this.editMode = null;
            }
        },

        saveCreatingCheckpoint(latLng) {
            this.$emit('checkpoint-create', {lat: latLng.lat, lng: latLng.lng});
        },

        /**
         * Opens the file explorer on the users machine to select a GPX file for uploading a track.
         */
        chooseGpxFile() {
            this.$refs.gpxUploader.click();
        },

        /**
         * Creates tracks based on the user's selected GPX files.
         *
         * @param event
         */
        handleUploadedGpxFile(event) {
            const files = event.target.files;
            if (files && files[0]) {
				this.isLoading = true;
				this.$apiFetch('/v1/activity-files/preview', {
					method: 'POST',
					params: {
						file: files[0]
					}
				}).then(data => {
					if (this.replacingTrack) {
						this.$emit('track-update', {
							track: this.replacingTrack,
							data: {
								file: files[0],
							},
						})
						this.replacingTrack = null;
					} else {
						this.$emit('track-create', {
							name: data.data.title,
							description: data.data.description,
							file: files[0],
						});
					}

				}).finally(() => {
					// Clear the uploader input, so we can upload the same file twice
					this.$refs.gpxUploader.value = '';

					this.editMode = null;
					this.isLoading = false;
				})
            }
        },

        replaceTrackLatLngsByFile(track) {
            this.replacingTrack = track;
            this.chooseGpxFile();
        },

        showEditModeHelpAlert(editMode) {
            if (!editMode || editMode === MapEditMode.UploadTrack || ([MapEditMode.CreateMarker].indexOf(editMode) > -1 && this.checkpoints && this.checkpoints.length)
                || ([MapEditMode.MoveTrack, MapEditMode.MoveTrackSpot, MapEditMode.MoveMarker].indexOf(editMode) > -1 && this.checkpoints && this.checkpoints.length)
                || ([MapEditMode.CreateTrack].indexOf(editMode) > -1 && this.tracks && this.tracks.length)
                || ([MapEditMode.CreateTrackSpotResult].indexOf(editMode) > -1 && this.trackSpots && this.trackSpots.length && this.trackSpots.filter(trackSpot => trackSpot.type === TrackSpotType.Result).length)
                || ([MapEditMode.CreateTrackSpotControl].indexOf(editMode) > -1 && this.trackSpots && this.trackSpots.length && this.trackSpots.filter(trackSpot => trackSpot.type === TrackSpotType.Control).length)
                || ([MapEditMode.CreateTrackSpotNavigation].indexOf(editMode) > -1 && this.trackSpots && this.trackSpots.length && this.trackSpots.filter(trackSpot => trackSpot.type === TrackSpotType.Navigation).length)) {
                return;
            }

            this.$alert({
                title: this.$t(`label.map_modes.${editMode}`),
                text: this.$t(`text.map_modes.${editMode}`),
                type: 'info',
                confirmText: 'OK'
            })
        },

        handleTrackClick(track, latLngData) {
            this.lastTrackClickLatLng = latLngData;

            const trackSpotData = {
                track_id: track.id,
                color: track.color,
                lat: latLngData.lat,
                lng: latLngData.lng
            };

            if ([this.editModes.CreateTrackSpotResult, this.editModes.CreateTrackSpotControl, this.editModes.CreateTrackSpotNavigation].indexOf(this.editMode) > -1) {

                switch (this.editMode) {
                    case this.editModes.CreateTrackSpotNavigation:
                        trackSpotData.type = TrackSpotType.Navigation;
                        break;
                    case this.editModes.CreateTrackSpotResult:
                        trackSpotData.type = TrackSpotType.Result;
                        trackSpotData.subtype = TrackSpotSubtype.ResultTimeMeasurement;
                        trackSpotData.is_visible = 1;
                        break;
                    case this.editModes.CreateTrackSpotControl:
                        trackSpotData.type = TrackSpotType.Control;
                        trackSpotData.is_visible = 1;
                        break;
                }

                if (track.participationClasses && track.participationClasses.length) {
                    const distanceFromStart = latLngData.distance_from_start;
                    trackSpotData['participation_classes'] = track.participationClasses.map(participationClass => {

                        // If the start spot of the participation class is equal to the track's start spot, we can use the provided distance from start to suggest a better intermediate position
                        if (distanceFromStart) {
                            const startSpot = this.trackSpots.length ? this.trackSpots.find(trackSpot => {
                                return trackSpot.type === TrackSpotType.Result && trackSpot.participationClasses && trackSpot.participationClasses.length && trackSpot.participationClasses.find(trackSpotParticipationClass => trackSpotParticipationClass.id === participationClass.id && trackSpotParticipationClass.pivot && trackSpotParticipationClass.pivot.position === 1);
                            }) : null;
                            if (startSpot && GeolocationService.isSame({lat: startSpot.lat, lng: startSpot.lng}, {lat: track.latlngs[0].lat, lng: track.latlngs[0].lng}, 10)) {
                                const otherTrackSpots = this.trackSpots.length ? this.trackSpots.filter(trackSpot => {
                                    return trackSpot.type === trackSpotData.type && trackSpot.participationClasses && trackSpot.participationClasses.length && trackSpot.participationClasses.find(trackSpotParticipationClass => trackSpotParticipationClass.id === participationClass.id);
                                }) : [];

                                const otherTrackSpotParticipationClasses = otherTrackSpots.map(trackSpot => {
                                    return trackSpot.participationClasses.find(trackSpotParticipationClass => trackSpotParticipationClass.id === participationClass.id);
                                }).sort((spotA, spotB) => spotA.pivot.distance_from_start > spotB.pivot.distance_from_start ? 1 : -1);

                                let insertIndex = otherTrackSpotParticipationClasses.findIndex(spot => spot.pivot.distance_from_start > distanceFromStart);
                                insertIndex = insertIndex > -1 ? insertIndex : otherTrackSpotParticipationClasses.length;

                                return {id: participationClass.id, position: insertIndex + 1, distance_from_start: distanceFromStart}
                            }
                        }

                        // Let the user explicitly select the position to prevent unseen position issues
                        return {id: participationClass.id, position: null}
                    });
                }

                this.$emit('track-spot-create', trackSpotData)
            } else if (this.editMode === this.editModes.MoveTrackSpot) {
				this.moveElement.lat = trackSpotData.lat;
				this.moveElement.lng = trackSpotData.lng;
				this.moveElement.track_id = track.id;
				this.moveElement.color = track.color;
			} else if (!this.editMode) {
				this.selectedTrack = track;
                this.$emit('track-click', trackSpotData)
				this.$nextTick(() => {
					this.isTrackDetailsModalVisible = true;
				});
            }
        },

		handleMovingTrack(track, trackData) {
			this.moveElement.latlngs = trackData.latlngs;
		},

		saveMovingTrack() {
			this.originalMovingTrackData = {latlngs: this.moveElement.latlngs};
			this.$emit('track-moved', {track: this.moveElement, data: this.originalMovingTrackData});
			this.cancelMovingTrack();
		},

		cancelMovingTrack() {
			this.moveElement.latlngs = this.originalMovingTrackData.latlngs;
			this.moveElement = null;
			this.editMode = null;
		},

        saveMoveChanges() {
			switch (this.editMode) {
				case MapEditMode.MoveTrack:
					this.saveMovingTrack();
					break;
				case MapEditMode.MoveMarker:
					this.saveMovingCheckpoint();
					break;
				case MapEditMode.MoveTrackSpot:
					this.saveMovingTrackSpot();
					break;
			}
        },

		cancelMoveChanges() {
			switch (this.editMode) {
				case MapEditMode.MoveTrack:
					this.cancelMovingTrack();
					break;
				case MapEditMode.MoveMarker:
					this.cancelMovingCheckpoint();
					break;
				case MapEditMode.MoveTrackSpot:
					this.cancelMovingTrackSpot();
					break;
			}
		},

		downloadTrack(track) {
			this.isTrackDetailsModalVisible = false;
			this.$emit('track-download', track);
		},

		editTrack(track) {
			this.isTrackDetailsModalVisible = false;
			this.$emit('track-edit', track);
		},

		destroyTrack(track) {
			this.isTrackDetailsModalVisible = false;
			this.$emit('track-destroy', track);
		},

		moveTrack(track) {
			this.isTrackDetailsModalVisible = false;
			this.prepareEditMode = null; // Just ensure to be null

			// Set a large min zoom when moving tracks to increase performance when rendering move markers
			if (this.lastTrackClickLatLng) {
				this.flyTo(this.lastTrackClickLatLng, true, this.mapZoom >= 16 ? this.mapZoom : 16);
				if (this.mapZoom >= 16) {
					this.editMode = MapEditMode.MoveTrack;
				} else {
					this.prepareEditMode = MapEditMode.MoveTrack; // Sets the edit mode after map moved / zoomed
				}
			} else {
				this.editMode = MapEditMode.MoveTrack;
			}

			this.originalMovingTrackData = {latlngs: track.latlngs};
			this.moveElement = track;
		},

		handleCheckpointClick(checkpoint) {
			this.selectedCheckpoint = checkpoint;
			this.$nextTick(() => {
				this.isCheckpointDetailsModalVisible = true;
			});
		},

		editCheckpoint(checkpoint) {
			this.isCheckpointDetailsModalVisible = false;
			this.$emit('checkpoint-edit', checkpoint);
		},

		destroyCheckpoint(checkpoint) {
			this.isCheckpointDetailsModalVisible = false;
			this.$emit('checkpoint-destroy', checkpoint);
		},

		moveCheckpoint(checkpoint) {
			this.isCheckpointDetailsModalVisible = false;
			this.prepareEditMode = null; // Just ensure to be null

			this.editMode = MapEditMode.MoveMarker;
			this.originalMovingCheckpointData = {lat: checkpoint.lat, lng: checkpoint.lng};
			this.moveElement = checkpoint;
		},

		saveMovingCheckpoint() {
			this.originalMovingCheckpointData = {lat: this.moveElement.lat, lng: this.moveElement.lng};
			this.$emit('checkpoint-moved', {checkpoint: this.moveElement, data: this.originalMovingCheckpointData});
			this.cancelMovingCheckpoint();
		},

		cancelMovingCheckpoint() {
			this.moveElement.lat = this.originalMovingCheckpointData.lat;
			this.moveElement.lng = this.originalMovingCheckpointData.lng;
			this.moveElement = null;
			this.editMode = null;
		},

		handleTrackSpotClick(trackSpot) {
			this.selectedTrackSpot = trackSpot;
			this.$nextTick(() => {
				this.isTrackSpotDetailsModalVisible = true;
			});
		},

		editTrackSpot(trackSpot) {
			this.isTrackSpotDetailsModalVisible = false;
			this.$emit('track-spot-edit', trackSpot);
		},

		destroyTrackSpot(trackSpot) {
			this.isTrackSpotDetailsModalVisible = false;
			this.$emit('track-spot-destroy', trackSpot);
		},

		moveTrackSpot(trackSpot) {
			this.isTrackSpotDetailsModalVisible = false;
			this.prepareEditMode = null; // Just ensure to be null

			// Set a large min zoom when moving track spots to be sure to hit the track polylines as required for the distance calculation
			if (trackSpot.lat && trackSpot.lng) {
				this.flyTo(trackSpot, true, 18);
				if (this.mapZoom === 18) {
					this.editMode = MapEditMode.MoveTrackSpot;
				} else {
					this.prepareEditMode = MapEditMode.MoveTrackSpot; // Sets the edit mode after map moved / zoomed
				}
			} else {
				this.editMode = MapEditMode.MoveTrackSpot;
			}

			this.originalMovingTrackSpotData = {lat: trackSpot.lat, lng: trackSpot.lng, track_id: trackSpot.track_id, color: trackSpot.color};
			this.moveElement = trackSpot;
		},

		saveMovingTrackSpot() {
			this.originalMovingTrackSpotData = {lat: this.moveElement.lat, lng: this.moveElement.lng, track_id: this.moveElement.track_id, color: this.moveElement.color};
			this.$emit('track-spot-moved', {trackSpot: this.moveElement, data: this.originalMovingTrackSpotData});
			this.cancelMovingTrackSpot();
		},

		cancelMovingTrackSpot() {
			this.moveElement.lat = this.originalMovingTrackSpotData.lat;
			this.moveElement.lng = this.originalMovingTrackSpotData.lng;
			this.moveElement.track_id = this.originalMovingTrackSpotData.track_id;
			this.moveElement.color = this.originalMovingTrackSpotData.color;
			this.moveElement = null;
			this.editMode = null;
		},

		async printMap() {
			this.isPrinting = true;
			const {BrowserPrint, BrowserPrintMode, BrowserPrintModeNames} = await import('./print');
			this.$nextTick(() => {
				const browserPrint = new BrowserPrint(this.mapObject, {
					documentTitle: this.event.display_name,
				}, {
					onCancel: () => {
						this.isPrinting = false;
					},
					onPrintEnd: () => {
						this.isPrinting = false;
					},
				})
				browserPrint.print(new BrowserPrintMode(BrowserPrintModeNames.Auto));
			});
		}
    }
}
</script>